(() => {
  if (window.SolviScentShelf?.initialized) return;

  let swym = null;
  let swymReady = false;
  let fetchInProgress = false;

  /*
    Product-level map:
    Shopify product ID -> actual Swym wishlist item.

    We deliberately key by product rather than variant so an older
    save still shows as saved even if Swym has a different variant.
  */
  const savedItems = new Map();

  const roots = new Set();


  /* =========================================================
     HELPERS
     ========================================================= */

  const productFromButton = (button) => {
    const card = button.closest('[data-solvi-product-card]');

    if (!card) return null;

    const productId = String(
      card.dataset.productId ||
      button.dataset.productId ||
      ''
    ).trim();

    const variantId = String(
      card.dataset.wishlistVariantId ||
      button.dataset.wishlistVariantId ||
      ''
    ).trim();

    const handle = String(
      card.dataset.productHandle ||
      button.dataset.productHandle ||
      ''
    ).trim();

    const title = String(
      card.dataset.productTitle ||
      button.dataset.productTitle ||
      'this scent'
    ).trim();

    const url = String(
      card.dataset.productUrl ||
      button.dataset.productUrl ||
      ''
    ).trim();

    const image = String(
      card.dataset.productImage ||
      button.dataset.productImage ||
      ''
    ).trim();

    if (!productId || !variantId) {
      return null;
    }

    return {
      productId,
      variantId,
      handle,
      title,
      url,
      image
    };
  };


  const normalizeFetchedItems = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.products)) return response.products;
    if (Array.isArray(response?.wishlistItems)) return response.wishlistItems;
    if (Array.isArray(response?.data?.items)) return response.data.items;

    return [];
  };


  const productIdFromWishlistItem = (item) =>
    String(
      item?.empi ||
      item?.productId ||
      item?.product_id ||
      ''
    ).trim();


  const setButtonState = (button, isSaved) => {
    const product = productFromButton(button);

    if (!product) return;

    button.setAttribute(
      'aria-pressed',
      String(isSaved)
    );

    button.setAttribute(
      'aria-label',
      isSaved
        ? `Remove ${product.title} from My Scent Shelf`
        : `Save ${product.title} to My Scent Shelf`
    );

    button.classList.toggle(
      'is-saved',
      isSaved
    );

    const text = button.querySelector(
      '[data-solvi-save-text]'
    );

    if (text) {
      text.textContent = isSaved
        ? button.dataset.solviSavedText || 'On my shelf'
        : button.dataset.solviUnsavedText || 'Save to shelf';
    }
  };


  const setButtonBusy = (button, busy) => {
    button.classList.toggle(
      'is-loading',
      busy
    );

    if (busy) {
      button.setAttribute(
        'aria-busy',
        'true'
      );

      button.disabled = true;
    } else {
      button.removeAttribute(
        'aria-busy'
      );

      button.disabled = false;
    }
  };


  const syncButton = (button) => {
    const product = productFromButton(button);

    if (!product) return;

    const isSaved = savedItems.has(
      product.productId
    );

    setButtonState(
      button,
      isSaved
    );
  };


  const syncAllButtons = () => {
    document
      .querySelectorAll('[data-solvi-save]')
      .forEach(syncButton);
  };


  const pushEvent = (
    eventName,
    detail = {}
  ) => {
    const payload = {
      event: eventName,
      ...detail
    };

    if (
      Array.isArray(window.dataLayer)
    ) {
      window.dataLayer.push(payload);
    }

    window.dispatchEvent(
      new CustomEvent(
        `solvi:${eventName}`,
        {
          detail: payload
        }
      )
    );
  };


  /* =========================================================
     FETCH REAL SWYM SHELF
     ========================================================= */

  const fetchShelf = () => {
    if (
      !swymReady ||
      !swym ||
      typeof swym.fetch !== 'function' ||
      fetchInProgress
    ) {
      return;
    }

    fetchInProgress = true;

    try {
      swym.fetch(
        (response) => {
          fetchInProgress = false;

          savedItems.clear();

          const items =
            normalizeFetchedItems(
              response
            );

          items.forEach((item) => {
            const productId =
              productIdFromWishlistItem(
                item
              );

            if (productId) {
              savedItems.set(
                productId,
                item
              );
            }
          });

          syncAllButtons();

          window.dispatchEvent(
            new CustomEvent(
              'solvi:shelf-synced',
              {
                detail: {
                  count:
                    savedItems.size
                }
              }
            )
          );
        },

        () => {
          fetchInProgress = false;

          /*
            Don't replace a shopper's current button states
            with fake local data if Swym happens to fail.
          */
          syncAllButtons();
        }
      );
    } catch (_error) {
      fetchInProgress = false;
    }
  };


  /* =========================================================
     ADD
     ========================================================= */

  const addToShelf = (
    button,
    product
  ) => {
    if (
      !swym ||
      typeof swym.addToWishList !==
        'function'
    ) {
      setButtonBusy(
        button,
        false
      );

      return;
    }

    const payload = {
  empi: Number(
    product.productId
  ),

  epi: Number(
    product.variantId
  ),

  du: new URL(
    product.url ||
      `/products/${product.handle}`,
    window.location.origin
  ).href,

  dt: product.title
};

    if (product.image) {
      payload.iu =
        product.image;
    }

    setButtonBusy(
      button,
      true
    );

    try {
      swym.addToWishList(
        payload,

        (response) => {
          /*
            Some Swym responses return the saved
            item directly; some do not.

            Store the response if useful, otherwise
            the original payload is sufficient until
            the next fetch.
          */
          const savedItem =
            response &&
            typeof response === 'object'
              ? response
              : payload;

          savedItems.set(
            product.productId,
            savedItem
          );

          setButtonBusy(
            button,
            false
          );

          syncAllButtons();

          pushEvent(
            'shelf_add',
            {
              product_id:
                product.productId,

              variant_id:
                product.variantId,

              product_handle:
                product.handle
            }
          );


          /*
            Re-read Swym after the add so our local map
            contains the exact item Swym expects if the
            shopper removes it afterward.
          */
          window.setTimeout(
            fetchShelf,
            100
          );
        },

        () => {
          setButtonBusy(
            button,
            false
          );

          syncButton(
            button
          );
        }
      );
    } catch (_error) {
      setButtonBusy(
        button,
        false
      );

      syncButton(
        button
      );
    }
  };


  /* =========================================================
     REMOVE
     ========================================================= */

  const removeFromShelf = (
    button,
    product
  ) => {
    if (
      !swym ||
      typeof swym.removeFromWishList !==
        'function'
    ) {
      setButtonBusy(
        button,
        false
      );

      return;
    }

    /*
      Prefer the real item returned by swym.fetch().
      This makes removal work even if an older save used
      a different variant from today's preferred variant.
    */
    const savedItem =
      savedItems.get(
        product.productId
      );

    if (!savedItem) {
      fetchShelf();

      setButtonBusy(
        button,
        false
      );

      return;
    }

    setButtonBusy(
      button,
      true
    );

    try {
      swym.removeFromWishList(
        savedItem,

        () => {
          savedItems.delete(
            product.productId
          );

          setButtonBusy(
            button,
            false
          );

          syncAllButtons();

          pushEvent(
            'shelf_remove',
            {
              product_id:
                product.productId,

              variant_id:
                product.variantId,

              product_handle:
                product.handle
            }
          );

          window.setTimeout(
            fetchShelf,
            100
          );
        },

        () => {
          setButtonBusy(
            button,
            false
          );

          syncButton(
            button
          );
        }
      );
    } catch (_error) {
      setButtonBusy(
        button,
        false
      );

      syncButton(
        button
      );
    }
  };


  /* =========================================================
     BUTTON CLICK
     ========================================================= */

  const handleSaveClick = (
    button
  ) => {
    const product =
      productFromButton(
        button
      );

    if (!product) return;

    /*
      If Swym hasn't initialized yet, don't create
      a second/local wishlist. Wait for the real shelf.
    */
    if (!swymReady) {
      button.setAttribute(
        'aria-busy',
        'true'
      );

      window.setTimeout(
        () =>
          button.removeAttribute(
            'aria-busy'
          ),
        700
      );

      return;
    }

    if (
      savedItems.has(
        product.productId
      )
    ) {
      removeFromShelf(
        button,
        product
      );
    } else {
      addToShelf(
        button,
        product
      );
    }
  };


  /* =========================================================
     INITIALIZE BUTTONS
     ========================================================= */

  const initialize = (
    container = document
  ) => {
    if (!container) return;

    roots.add(container);

    const buttons = [];

    if (
      container.matches?.(
        '[data-solvi-save]'
      )
    ) {
      buttons.push(
        container
      );
    }

    container
      .querySelectorAll?.(
        '[data-solvi-save]'
      )
      .forEach((button) => {
        buttons.push(button);
      });

    buttons.forEach(
      (button) => {
        if (
          button.dataset
            .solviShelfReady ===
          'true'
        ) {
          syncButton(button);
          return;
        }

        button.dataset
          .solviShelfReady =
          'true';

        syncButton(button);

        button.addEventListener(
          'click',
          (event) => {
            /*
              Save buttons are buttons rather than
              links, but prevent bubbling into card
              links/quick actions just in case.
            */
            event.preventDefault();
            event.stopPropagation();

            handleSaveClick(
              button
            );
          }
        );
      }
    );

    if (swymReady) {
      fetchShelf();
    }
  };


  /* =========================================================
     ACCEPT SWYM SDK
     ========================================================= */

  const acceptSwym = (
    candidate
  ) => {
    if (
      !candidate ||
      typeof candidate.fetch !==
        'function'
    ) {
      return;
    }

    swym = candidate;
    swymReady = true;

    document
      .querySelectorAll(
        '[data-solvi-save][aria-busy="true"]'
      )
      .forEach((button) =>
        button.removeAttribute(
          'aria-busy'
        )
      );

    fetchShelf();
  };


  /* =========================================================
     PUBLIC API
     ========================================================= */

  window.SolviScentShelf = {
    initialized: true,

    initialize,

    refresh: fetchShelf,

    isSaved(productId) {
      return savedItems.has(
        String(productId)
      );
    }
  };


  /* =========================================================
     START
     ========================================================= */

  const start = () => {
    initialize(document);


    /*
      Your custom PDP integration already emits
      this bridge when Swym is available.
    */
    window.addEventListener(
      'solvi:swym-ready',
      (event) => {
        acceptSwym(
          event.detail?.swat
        );
      }
    );


    /*
      Native Swym initialization.
    */
    window.SwymCallbacks =
      window.SwymCallbacks || [];

    window.SwymCallbacks.push(
      (sdk) => {
        acceptSwym(sdk);
      }
    );


    /*
      Swym may already have initialized before this
      file executed.
    */
    acceptSwym(
      window.solviSwymSdk ||
      window._swat
    );
  };


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }


  /*
    Collection sections can be re-rendered by Shopify
    without a full page refresh.
  */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialize(
        event.target
      );
    }
  );


  /*
    Catch dynamically inserted product cards.
  */
  const observe = () => {
    if (!document.body) return;

    const observer =
      new MutationObserver(
        (mutations) => {
          mutations.forEach(
            (mutation) => {
              mutation.addedNodes.forEach(
                (node) => {
                  if (
                    node.nodeType === 1
                  ) {
                    initialize(
                      node
                    );
                  }
                }
              );
            }
          );
        }
      );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  };


  if (document.body) {
    observe();
  } else {
    document.addEventListener(
      'DOMContentLoaded',
      observe,
      { once: true }
    );
  }
})();